import * as tslib_1 from "tslib";
/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { Text } from "./Text";
import { CloseButton } from "./IconButton";
import { useTransition, animated } from "react-spring";
import { Overlay } from "./Overlay";
import { useFocusElement } from "./Hooks/use-focus-trap";
import PropTypes from "prop-types";
import useScrollLock from "use-scroll-lock";
import { useTheme } from "./Theme/Providers";
/**
 * A dialog is useful for displaying infomation that
 * commands the user's attention.
 */
export var Dialog = function (_a) {
    var isOpen = _a.isOpen, onRequestClose = _a.onRequestClose, mobileFullscreen = _a.mobileFullscreen, title = _a.title, children = _a.children, other = tslib_1.__rest(_a, ["isOpen", "onRequestClose", "mobileFullscreen", "title", "children"]);
    var theme = useTheme();
    var transitions = useTransition(isOpen, null, {
        from: { opacity: 0, transform: "scale(0.9)" },
        enter: { opacity: 1, transform: "scale(1)" },
        leave: { opacity: 0, transform: "scale(0.9)" },
        config: { mass: 1, tension: 185, friction: 26 }
    });
    var scrollableRef = React.useRef(null);
    var ref = React.useRef(null);
    useFocusElement(ref, isOpen);
    useScrollLock(isOpen, scrollableRef);
    return (jsx(React.Fragment, null,
        jsx(Overlay, { onRequestClose: onRequestClose, isOpen: isOpen },
            jsx(React.Fragment, null, transitions.map(function (_a) {
                var _b, _c;
                var item = _a.item, key = _a.key, animationProps = _a.props;
                return item && (jsx(animated.div, tslib_1.__assign({ key: key, className: "Dialog", "aria-modal": "true", ref: ref, tabIndex: -1, onClick: function (e) {
                        e.stopPropagation();
                    }, style: {
                        opacity: animationProps.opacity,
                        transform: animationProps.transform
                    }, css: [
                        (_b = {
                                zIndex: theme.zIndices.modal,
                                background: theme.colors.background.default,
                                boxShadow: theme.shadows.md,
                                borderRadius: theme.radii.lg,
                                margin: "16px",
                                width: "calc(100% - 32px)",
                                outline: "none"
                            },
                            _b[theme.mediaQueries.sm] = {
                                maxWidth: "500px",
                                margin: "30px auto"
                            },
                            _b[theme.mediaQueries.lg] = {
                                maxWidth: "650px",
                                margin: "30px auto"
                            },
                            _b),
                        mobileFullscreen && (_c = {
                                maxWidth: "none",
                                margin: 0,
                                width: "100vw",
                                height: "100vh",
                                borderRadius: 0,
                                boxShadow: "none"
                            },
                            _c[theme.mediaQueries.sm] = {
                                maxWidth: "none",
                                margin: "0"
                            },
                            _c[theme.mediaQueries.md] = {
                                maxWidth: "500px",
                                margin: "30px auto",
                                height: "auto",
                                boxShadow: theme.shadows.md,
                                borderRadius: theme.radii.lg,
                                width: "calc(100% - 32px)"
                            },
                            _c)
                    ] }, other),
                    jsx(React.Fragment, null,
                        title && (jsx(DialogHeader, { className: "Dialog__header", css: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: theme.spaces.lg + " " + theme.spaces.lg + " 0 " + theme.spaces.lg
                            }, title: title, onRequestClose: onRequestClose })),
                        jsx("div", { ref: scrollableRef }, children))));
            })))));
};
Dialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    mobileFullscreen: PropTypes.bool,
    children: PropTypes.node
};
export var DialogHeader = function (_a) {
    var title = _a.title, onRequestClose = _a.onRequestClose, other = tslib_1.__rest(_a, ["title", "onRequestClose"]);
    return (jsx("div", tslib_1.__assign({}, other),
        jsx(Text, { wrap: false, variant: "h4" }, title),
        onRequestClose && jsx(CloseButton, { onClick: onRequestClose })));
};
DialogHeader.propTypes = {
    title: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func
};
